import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Check, ExternalLink, Twitter, Linkedin, Facebook, Instagram } from "lucide-react";

const firstUrl = (t = "") => (t.match(/https?:\/\/\S+/) || [""])[0];

const PLATFORM_META = {
  twitter: { label: "X", icon: Twitter, web: true },
  linkedin: { label: "LinkedIn", icon: Linkedin, web: true },
  facebook: { label: "Facebook", icon: Facebook, web: true },
  instagram: { label: "Instagram", icon: Instagram, web: false },
  tiktok: { label: "TikTok", icon: ExternalLink, web: false },
};

// One-tap publishing with NO account connection: copies the caption and opens
// the platform's own composer prefilled (user is already logged in there).
export const QuickPublish = ({ platform, text = "", url = "" }) => {
  const [copied, setCopied] = useState(false);
  const meta = PLATFORM_META[platform] || PLATFORM_META.twitter;
  const link = url || firstUrl(text);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Caption copied");
    } catch { toast.error("Couldn't copy"); }
  };

  const openIn = async () => {
    let href;
    if (platform === "twitter") {
      href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text || "")}`;
    } else if (platform === "linkedin") {
      href = link ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` : "https://www.linkedin.com/feed/";
      if (!link) { await copy(); toast.info("Caption copied — paste it into LinkedIn."); }
    } else if (platform === "facebook") {
      href = link ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}` : "https://www.facebook.com/";
      if (!link) { await copy(); toast.info("Caption copied — paste it into Facebook."); }
    } else {
      href = "https://www.facebook.com/"; // unreachable for non-web platforms
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const Icon = meta.icon;

  return (
    <div className="flex items-center gap-2 pt-2 border-t border-border" data-testid={`quick-publish-${platform}`}>
      <Button size="sm" variant="outline" onClick={copy} className="h-8 text-xs border-border" data-testid={`quick-copy-${platform}`}>
        {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
        {copied ? "Copied" : "Copy caption"}
      </Button>
      {meta.web ? (
        <Button size="sm" onClick={openIn} className="h-8 text-xs bg-primary hover:bg-primary/90 text-white" data-testid={`quick-open-${platform}`}>
          <Icon className="h-3.5 w-3.5 mr-1.5" /> Open in {meta.label}
        </Button>
      ) : (
        <span className="text-[11px] text-muted-foreground">Copy, then paste into the {meta.label} app</span>
      )}
    </div>
  );
};

export default QuickPublish;
